import { BodySmall, SearchIcon, IconButton, Box, Tooltip, CircularProgress, BodyXS, InfoOutlinedIcon, BodyMedium, Button, HistoryOutlinedIcon } from '@parspec/pixel';
import { CustomNoRowsOverlayProps } from '@ag-grid-community/react';
import { ICellRendererParams } from 'ag-grid-community';

import { RouterLink } from '../../shared/RouterLink';
import { getRouteFromLocalStorage, setRouteToLocalStorage } from '../../shared/utils/utils';
import ImportProducts from './ImportProducts';
import { BOM_TABLES_ENUM, ROW_LEVEL } from './constants';
import { OptionDataType } from '../pricing&LeadTime/optionsTable';
import { AddOrDeductFooterTemplate } from '../pricing&LeadTime/optionsTable/utils';
import { SubtotalsNotApplicable } from '../pricing&LeadTime/shared/SubtotalsNotApplicable';


export const ProductFinderTemplate = (props: any) => {
    const getRoute = getRouteFromLocalStorage()
    function handleClick() {
        setRouteToLocalStorage(`${getRoute}`);
    }
    if (props?.node?.rowPinned === 'bottom') {
        return null;
    }
    if (props?.node?.level === 0) {
        return null;
    }
    return (
        <Box display="flex" justifyContent={'center'} width={1}>
            <RouterLink to={`productFinder/${props?.data?.id}`}>
                <IconButton onClick={handleClick}>
                    <SearchIcon color="primary" sx={{ width: 16, height: 16 }} />
                </IconButton>
            </RouterLink>
        </Box>
    );
};

export const ProductFinderDisabledTemplate = (params: ICellRendererParams) => {
    if (params?.node?.rowPinned === 'bottom' || params?.data?.is_kit) {
        return null;
    }
    return (
        <Box display="flex" justifyContent={'center'} width={1}>
            <Box margin="auto">
                <Tooltip placement="top" title="Enable Product Finder access under the user management screen to access this feature">
                    <SearchIcon color="neutral" sx={{ width: 16, height: 16 }} />
                </Tooltip>
            </Box>
        </Box>
    );
};

export const LoadingOverlayComponent = () => {
    return (
        <Box className="ag-loading-center">
            <Box sx={{ opacity: 1 }} aria-label="loading">
                <CircularProgress size="lg" color={'primary'} />
            </Box>
        </Box>
    );
};

interface NoRowsOverlayProps extends CustomNoRowsOverlayProps {
    onEnterManually: () => Promise<void>;
};

const sxStyles = {
    container: {
        position: 'absolute',
        top: 33,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: "column",
        alignItems: 'center',
        justifyContent: 'center',
        gap: "16px",
        backgroundColor: 'neutral.light',
        zIndex: 10,
        pointerEvents: "all"
    },
    description: {
        lineHeight: "1.25rem",
    }
}

export const NoRowsOverlayWithButtons = (props: NoRowsOverlayProps) => {
    const { onEnterManually } = props;
    return (
        <Box sx={sxStyles.container}>
            <img src={'assets/images/NoData/NoData.svg'} alt="No-Data" height={100} />
            <Box>
                <BodyMedium fontWeight={700}>Looking a little empty!</BodyMedium>
                <Box width="600px" mt={1}>
                    <BodySmall sx={sxStyles.description}>
                        To get started, add products manually or let us do the heavy lifting by
                    </BodySmall>
                    <BodySmall sx={sxStyles.description}>
                        importing a schedule, quote, Parspec BOM, or submittal.
                    </BodySmall>
                </Box>
            </Box>
            <Box display="flex" gap={2}>
                <Button
                    color="secondary"
                    variant="outlined"
                    sx={{ bgcolor: "white" }}
                    onClick={onEnterManually}
                >
                    Enter Manually
                </Button>
                <ImportProducts enableOptionSections={false} />
            </Box>

        </Box>
    );
};

export const NoRowsOverlayComponent = (
    props: CustomNoRowsOverlayProps & { helperText: string, isTableHaveFooter?: boolean }
) => {
    const { isTableHaveFooter, helperText } = props;
    return (
        <Box className={`ag-overlay-no-rows-wrapper ${isTableHaveFooter ? '' : ' overlay-rows-footer'}`}>
            <BodySmall>No records to display</BodySmall>
            {!!helperText && <BodySmall>{helperText}</BodySmall>}
        </Box>
    );
};

export const PerCustomHeader = () => {

    return (
        <Box display={'flex'} gap={1} alignItems={'center'}>
            <Box >
                <BodyXS fontWeight={500}>{'Per'}</BodyXS>
            </Box>
            <Box display="flex" gap={2} alignItems="center">
                <Box>
                    <Tooltip placement="top" title={<span style={{ display: 'block', textAlign: 'center', }}>{"Quantity or measurement of unit(s) corresponding to the cost entered"}</span>}>
                        <Box color={'text.secondary'}>
                            <InfoOutlinedIcon color={'inherit'} fontSize="small" />
                        </Box>
                    </Tooltip>
                </Box>
            </Box>
        </Box>
    )
}

export const QtyTemplate = (params: ICellRendererParams) => {

    if (params?.node?.level === ROW_LEVEL.SECTION) {
        return params?.value
    }

    if (params?.node?.level === ROW_LEVEL.COMPONENT && params.data[BOM_TABLES_ENUM.QTY]) {
        return `${params.data[BOM_TABLES_ENUM.QTY]} per kit`
    }

    return params.data[BOM_TABLES_ENUM.QTY]
}

export const CustomHeader = ({ isProductFinder, isPricingHistory }: { isProductFinder?: boolean, isPricingHistory?: boolean }) => {
    if (isProductFinder) {
        return (
            <Box display={'flex'} gap={1} alignItems={'center'} width={"100%"} justifyContent={'center'}>
                <Box display={'flex'} >
                    <Tooltip placement="top" title={<span style={{ display: 'block', textAlign: 'center', }}>{"Product Finder"}</span>}>
                        <SearchIcon />
                    </Tooltip>
                </Box>
            </Box>
        );
    }

    if (isPricingHistory) {
        return (
            <Box display={'flex'} gap={1} alignItems={'center'}>
                <Box width="100%" display="flex" justifyContent="center" alignItems="center">
                    <Tooltip placement="top" title={<span style={{ display: 'block', textAlign: 'center', }}>{"Pricing History"}</span>}>
                        <HistoryOutlinedIcon color="secondary" fontSize="small" />
                    </Tooltip>
                </Box>
            </Box>
        );
    }

    return null;
};


export const getSectionalValueTemplate = (params: ICellRendererParams<OptionDataType>) => {
    const { node } = params;

    if (node.level === ROW_LEVEL.SECTION && node?.aggData?.extendedPrice === 'N/A') {
        return <SubtotalsNotApplicable title="Subtotals are not available because at least one included lot is present in multiple sections" />
    }

    return params.valueFormatted;
};


export const getAddDeductAmountTemplate = (params: ICellRendererParams<OptionDataType>) => {
    const { data, node } = params;

    const isComponentsTable = Boolean(data?.kit) && node?.level === ROW_LEVEL.COMPONENT;

    if (isComponentsTable) {
        return;
    }
    if (node.level === ROW_LEVEL.SECTION && node?.aggData?.addDeductAmount === 'N/A') {
        return <AddOrDeductFooterTemplate />;
    }

    return params.valueFormatted;
};